/**
 * IT Reports Panel — premium enterprise Reporting Center (rpt- design system).
 * IT-REPORTS-UI-001: two main tabs (Individual / Consolidated), banded sections,
 * 4-up asset grid, full-width horizontal IT Tickets row with From/To date range.
 * Report APIs, permissions, filters and export logic unchanged.
 */
import React, { useState, useEffect } from 'react';
import {
  FileText, Download, FileSpreadsheet, Loader, Package, Ticket, Shield, Clock, Wrench, BarChart3, LayoutGrid, Layers, Boxes, CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import './ReportsPanel.css';
import ProductInventoryReport from './ProductInventoryReport';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

const ICON_MAP = {
  asset_inventory: Package,
  asset_assignments: Layers,
  maintenance_report: Wrench,
  warranty_report: Shield,
  ticket_report: Ticket,
  audit_log: FileText,
  sla_compliance: Clock
};

const FORMAT_LABELS = { csv: 'CSV', excel: 'Excel', pdf: 'PDF' };

// Correct, per-report status vocabularies (backend enums)
const STATUS_OPTIONS = {
  asset_inventory: [
    ['available', 'Available'], ['assigned', 'Assigned'], ['maintenance', 'Maintenance'],
    ['pending_return', 'Pending Return'], ['retired', 'Retired']
  ],
  maintenance_report: [
    ['scheduled', 'Scheduled'], ['in_progress', 'In Progress'],
    ['completed', 'Completed'], ['cancelled', 'Cancelled']
  ],
  ticket_report: [
    ['open', 'Open'], ['in_progress', 'In Progress'], ['waiting_on_user', 'Waiting on User'],
    ['resolved', 'Resolved'], ['closed', 'Closed']
  ]
};

const PRIORITY_OPTIONS = [['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['urgent', 'Urgent']];

const Control = ({ label, children, className = '' }) => (
  <div className={`rpt-control ${className}`}><label>{label}</label>{children}</div>
);

/* `layout="row"` lays the controls out horizontally (full-width IT Tickets card). */
const ReportCard = ({ report, onGenerate, isGenerating, layout = 'grid' }) => {
  const formats = report.formats?.length ? report.formats : ['csv', 'pdf'];
  const [format, setFormat] = useState(formats[0]);
  const [filters, setFilters] = useState({});
  const Icon = ICON_MAP[report.id] || FileText;
  const statusOpts = STATUS_OPTIONS[report.id];
  const hasRange = report.filters?.includes('start_date') && report.filters?.includes('end_date');
  const rangeError = hasRange && filters.start_date && filters.end_date && filters.start_date > filters.end_date
    ? 'From Date cannot be later than To Date' : '';
  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  const generate = () => {
    if (rangeError) { toast.error(rangeError); return; }
    onGenerate(report, format, filters);
  };

  return (
    <div className={`rpt-tile rpt-tile-${layout}`} data-testid={`rpt-card-${report.id}`}>
      <div className="rpt-tile-head">
        <div className="rpt-tile-icon"><Icon size={17} /></div>
        <div className="rpt-tile-title">
          <h4>{report.name}</h4>
          <p>{report.description}</p>
        </div>
      </div>

      <div className="rpt-controls">
        <Control label="Format:">
          <div className="rpt-segmented" role="group">
            {formats.map(f => (
              <button key={f} type="button" className={format === f ? 'active' : ''} onClick={() => setFormat(f)}
                data-testid={`rpt-format-${report.id}-${f}`}>
                {FORMAT_LABELS[f] || f.toUpperCase()}
              </button>
            ))}
          </div>
        </Control>

        {report.filters?.includes('status') && statusOpts && (
          <Control label="Status:">
            <select value={filters.status || ''} onChange={set('status')} data-testid={`rpt-status-${report.id}`}>
              <option value="">All</option>
              {statusOpts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Control>
        )}

        {report.filters?.includes('priority') && (
          <Control label="Priority:">
            <select value={filters.priority || ''} onChange={set('priority')} data-testid={`rpt-priority-${report.id}`}>
              <option value="">All</option>
              {PRIORITY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Control>
        )}

        {hasRange && (
          <Control label="Date Range:" className="rpt-control-range">
            <div className="rpt-range" data-testid={`rpt-range-${report.id}`}>
              <label className={`rpt-date ${rangeError ? 'invalid' : ''}`}>
                <span>From Date</span>
                <CalendarDays size={14} className="rpt-date-ico" />
                <input type="date" value={filters.start_date || ''} max={filters.end_date || undefined} onChange={set('start_date')}
                  data-testid={`rpt-from-${report.id}`} />
              </label>
              <label className={`rpt-date ${rangeError ? 'invalid' : ''}`}>
                <span>To Date</span>
                <CalendarDays size={14} className="rpt-date-ico" />
                <input type="date" value={filters.end_date || ''} min={filters.start_date || undefined} onChange={set('end_date')}
                  data-testid={`rpt-to-${report.id}`} />
              </label>
            </div>
          </Control>
        )}

        {report.filters?.includes('days') && (
          <Control label="Period:">
            <select value={filters.days || 30} onChange={e => setFilters({ ...filters, days: parseInt(e.target.value) })}
              data-testid={`rpt-days-${report.id}`}>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </Control>
        )}

        {report.filters?.includes('days_until_expiry') && (
          <Control label="Expiring within:">
            <select value={filters.days_until_expiry || 90} onChange={e => setFilters({ ...filters, days_until_expiry: parseInt(e.target.value) })}
              data-testid={`rpt-expiry-${report.id}`}>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
            </select>
          </Control>
        )}

        {report.filters?.includes('include_history') && (
          <label className="rpt-checkbox">
            <input type="checkbox" checked={filters.include_history || false}
              onChange={e => setFilters({ ...filters, include_history: e.target.checked })} data-testid={`rpt-history-${report.id}`} />
            Include historical records
          </label>
        )}

        {layout === 'row' && (
          <button className="rpt-generate rpt-generate-inline" onClick={generate} disabled={isGenerating || !!rangeError}
            data-testid={`rpt-generate-${report.id}`}>
            {isGenerating ? <><Loader size={14} className="rpt-spin" /> Generating…</> : <><Download size={14} /> Generate Report</>}
          </button>
        )}
      </div>

      {rangeError && <p className="rpt-range-error" role="alert" data-testid={`rpt-range-error-${report.id}`}>{rangeError}</p>}

      {layout !== 'row' && (
        <button className="rpt-generate" onClick={generate} disabled={isGenerating} data-testid={`rpt-generate-${report.id}`}>
          {isGenerating ? <><Loader size={14} className="rpt-spin" /> Generating report...</> : <><Download size={14} /> Generate Report</>}
        </button>
      )}
    </div>
  );
};

const CategorySection = ({ id, icon: Icon, title, blurb, reports, cols, layout, onGenerate, generatingReport }) => {
  if (!reports.length) return null;
  return (
    <section className="rpt-section" data-testid={`rpt-section-${id}`}>
      <header className="rpt-section-head">
        <span className="rpt-section-icon"><Icon size={15} /></span>
        <h4>{title} <b>({reports.length})</b></h4>
        <p>– {blurb}</p>
      </header>
      <div className={`rpt-grid rpt-grid-${cols}`}>
        {reports.map(r => (
          <ReportCard key={r.id} report={r} layout={layout} onGenerate={onGenerate} isGenerating={generatingReport === r.id} />
        ))}
      </div>
    </section>
  );
};

const ReportsPanel = ({ isITManager }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(null);
  const [tab, setTab] = useState('individual');

  useEffect(() => {
    const fetchAvailableReports = async () => {
      try {
        const response = await fetch(`${API_URL}/api/it-reports/available`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
        if (response.ok) {
          const data = await response.json();
          setReports(data.reports || []);
        }
      } catch (error) {
        console.error('Failed to fetch reports:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAvailableReports();
  }, []);

  const handleGenerateReport = async (report, format, filters) => {
    setGeneratingReport(report.id);
    const toastId = toast.loading('Generating report...');
    try {
      const params = new URLSearchParams({ format });
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== undefined) params.append(key, value.toString());
      });

      const response = await fetch(`${API_URL}${report.endpoint}?${params.toString()}`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
      if (!response.ok) {
        let detail = 'Failed to generate report';
        try { detail = (await response.json()).detail || detail; } catch { /* non-JSON error body */ }
        throw new Error(detail);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `report_${Date.now()}.${format === 'excel' ? 'xlsx' : format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report ready — download started', { id: toastId });
    } catch (error) {
      toast.error(error.message || 'Report generation failed. Please try again.', { id: toastId });
      console.error(error);
    } finally {
      setGeneratingReport(null);
    }
  };

  if (loading) {
    return (
      <div className="rpt-loading" data-testid="rpt-loading">
        <Loader size={22} className="rpt-spin" />
        <p>Loading available reports...</p>
      </div>
    );
  }

  // IT-ASSET-REPORT-002: the consolidated product report has its own live preview panel (Consolidated tab)
  const assetReports = reports.filter(r => r.id !== 'asset_product_inventory' && (r.id.includes('asset') || r.id.includes('maintenance') || r.id.includes('warranty')));
  const ticketReports = reports.filter(r => r.id.includes('ticket'));
  const adminReports = isITManager ? reports.filter(r => r.id.includes('audit') || r.id.includes('sla')) : [];
  const individualCount = assetReports.length + ticketReports.length + adminReports.length;
  const showConsolidated = isITManager;

  return (
    <div className="rpt-panel" data-testid="reports-panel">
      <div className="rpt-header">
        <div className="rpt-header-left">
          <div className="rpt-header-icon"><BarChart3 size={19} /></div>
          <div>
            <h3>Reports &amp; Analytics</h3>
            <p>Generate operational, asset, ticket and compliance reports</p>
          </div>
        </div>
        <span className="rpt-count" data-testid="rpt-count">
          <FileSpreadsheet size={13} /> {reports.length} Report{reports.length === 1 ? '' : 's'} Available
        </span>
      </div>

      <div className="rpt-tabs" role="tablist" data-testid="rpt-tabs">
        <button type="button" role="tab" aria-selected={tab === 'individual'} className={tab === 'individual' ? 'active' : ''}
          onClick={() => setTab('individual')} data-testid="rpt-tab-individual">
          <LayoutGrid size={15} /> Individual Reports <span className="rpt-tab-badge">{individualCount}</span>
        </button>
        {showConsolidated && (
          <button type="button" role="tab" aria-selected={tab === 'consolidated'} className={tab === 'consolidated' ? 'active' : ''}
            onClick={() => setTab('consolidated')} data-testid="rpt-tab-consolidated">
            <Boxes size={15} /> Consolidated Reports <span className="rpt-tab-badge">1</span>
          </button>
        )}
      </div>

      {tab === 'individual' && (
        <div className="rpt-tab-body" role="tabpanel" data-testid="rpt-individual-panel">
          <CategorySection id="assets" icon={Package} title="Asset Reports" blurb="Asset inventory, assignments, maintenance and warranty reports"
            reports={assetReports} cols="4" onGenerate={handleGenerateReport} generatingReport={generatingReport} />
          <CategorySection id="tickets" icon={Ticket} title="Ticket Reports" blurb="IT support and service ticket reports"
            reports={ticketReports} cols="1" layout="row" onGenerate={handleGenerateReport} generatingReport={generatingReport} />
          <CategorySection id="admin" icon={Shield} title="Admin & Compliance" blurb="System audit and compliance reports"
            reports={adminReports} cols="2" onGenerate={handleGenerateReport} generatingReport={generatingReport} />
        </div>
      )}
      {tab === 'consolidated' && showConsolidated && (
        <div className="rpt-tab-body" role="tabpanel" data-testid="rpt-consolidated-panel">
          <ProductInventoryReport />
        </div>
      )}
    </div>
  );
};

export default ReportsPanel;
